import type {Icon} from '@primer/octicons-react'
import {
  CodeIcon,
  CodeSquareIcon,
  CommentDiscussionIcon,
  DiffIcon,
  FileDirectoryFillIcon,
  FileIcon,
  GitCommitIcon,
  GitMergeIcon,
  GitPullRequestClosedIcon,
  GitPullRequestDraftIcon,
  GitPullRequestIcon,
  ImageIcon,
  IssueClosedIcon,
  IssueOpenedIcon,
  LinkIcon,
  PaperclipIcon,
  RepoIcon,
  SkipIcon,
  SpaceIcon,
  TagIcon,
  WorkflowIcon,
} from '@primer/octicons-react'
import type {ComposerAttachmentIcon} from '@github-ui/agent-sessions-interface/components/chat-composer-attachments'

const ATTACHMENT_DETAIL_META_KEY = 'copilot.attachmentDetail'
const ATTACHMENT_UI_META_KEY = 'github.attachmentUi'

/** Structural subset of an AHP `MessageAttachment`, so this mapping needs no `@microsoft/agent-host-protocol` dependency. */
export interface AttachmentIconInput {
  readonly displayKind?: string
  readonly _meta?: Readonly<Record<string, unknown>>
}

// Inlined (not imported from agent-sessions-interface's ATTACHMENT_ICONS) to keep this module CSS-free.
const COMPOSER_ICON_GLYPHS = {
  file: FileIcon,
  folder: FileDirectoryFillIcon,
  repo: RepoIcon,
  commit: GitCommitIcon,
  discussion: CommentDiscussionIcon,
  diff: DiffIcon,
  release: TagIcon,
  snippet: CodeIcon,
  link: LinkIcon,
  workflow: WorkflowIcon,
  issue: IssueOpenedIcon,
  'issue-open': IssueOpenedIcon,
  'issue-closed': IssueClosedIcon,
  'issue-not-planned': SkipIcon,
  pull: GitPullRequestIcon,
  'pull-open': GitPullRequestIcon,
  'pull-closed': GitPullRequestClosedIcon,
  'pull-merged': GitMergeIcon,
  'pull-draft': GitPullRequestDraftIcon,
  space: SpaceIcon,
} as const satisfies Record<ComposerAttachmentIcon, Icon>

// Returns undefined for unrecognized kinds so attachmentToComposerIcon can apply the composer's
// 'file' fallback while attachmentToIcon falls through to the plain AHP kinds and a paperclip.
function classifyAttachment(attachment: AttachmentIconInput): ComposerAttachmentIcon | undefined {
  switch (attachment.displayKind) {
    case 'repository':
      return 'repo'
    case 'github-commit':
      return 'commit'
    case 'github-release':
      return 'release'
    case 'github-snippet':
      return 'snippet'
    case 'github-file':
      return 'file'
    case 'github-file-diff':
      return 'diff'
    case 'github-url':
      return 'link'
    case 'github-actions-job':
      return 'workflow'
  }

  const detail = attachment._meta?.[ATTACHMENT_DETAIL_META_KEY] as
    | {type?: string; referenceType?: string; state?: string}
    | undefined
  const uiMeta = attachment._meta?.[ATTACHMENT_UI_META_KEY] as {draft?: boolean} | undefined

  if (detail?.type === 'github_reference') {
    const state = detail.state
    if (detail.referenceType === 'pr') {
      if (uiMeta?.draft && (state === 'open' || !state)) return 'pull-draft'
      switch (state) {
        case 'open':
          return 'pull-open'
        case 'closed':
          return 'pull-closed'
        case 'merged':
          return 'pull-merged'
        default:
          return 'pull'
      }
    }
    if (detail.referenceType === 'discussion') return 'discussion'
    if (detail.referenceType === 'issue') {
      switch (state) {
        case 'open':
          return 'issue-open'
        case 'closed':
          return 'issue-closed'
        case 'not_planned':
          return 'issue-not-planned'
        default:
          return 'issue'
      }
    }
  }

  return undefined
}

/** State tint for a reference icon, mirroring the composer's colored subset. */
export type AttachmentIconColor = 'open' | 'done' | 'danger'

const ICON_COLORS: Partial<Record<ComposerAttachmentIcon, AttachmentIconColor>> = {
  'issue-open': 'open',
  'issue-closed': 'done',
  'pull-open': 'open',
  'pull-closed': 'danger',
  'pull-merged': 'done',
}

/** The `ComposerAttachmentIcon` key for a composer token. The seam for deduping copilot-chat-app's `implicitAttachmentIcon` later; not yet wired there. */
export function attachmentToComposerIcon(attachment: AttachmentIconInput): ComposerAttachmentIcon {
  return classifyAttachment(attachment) ?? 'file'
}

/** State tint for an attachment's icon (open/done/danger), or undefined for the muted default. Matches the composer's colored subset. */
export function attachmentToIconColor(attachment: AttachmentIconInput): AttachmentIconColor | undefined {
  const key = classifyAttachment(attachment)
  return key ? ICON_COLORS[key] : undefined
}

/** The octicon for an attachment chip in the AHP timeline (state-aware for GitHub references; paperclip for unknown kinds). */
export function attachmentToIcon(attachment: AttachmentIconInput): Icon {
  const key = classifyAttachment(attachment)
  if (key) return COMPOSER_ICON_GLYPHS[key]

  switch (attachment.displayKind) {
    case 'image':
      return ImageIcon
    case 'document':
      return FileIcon
    case 'symbol':
      return CodeSquareIcon
    case 'directory':
      return FileDirectoryFillIcon
    case 'selection':
      return CodeIcon
    default:
      return PaperclipIcon
  }
}
